import React, {Fragment, useEffect, useState} from "react";
import {
    CellContext,
    Column,
    ColumnDef,
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    Row as RowInfo, SortingState,
    Table as ReactTable,
    useReactTable
} from "@tanstack/react-table";
import DebouncedInput from "Components/Common/DebouncedInput";

import { Alert, Button, Col, Form, Modal, Row, Spinner, Table, OverlayTrigger, Tooltip } from "react-bootstrap";

import {Utils, Defaults} from "../../common/utils";

import ReportDetails from "./components/report-details";

import {Report} from "Stores/reportStore";
import {NavLink} from "react-router-dom";
import PageTitle from "Components/Common/PageTitle";
import {isWithinRangeFilter} from "../../common/filters";
import {useReports} from "@/pages/reports/hooks/reports";
import {EAIClient} from "Services/EAIClient";
import {useAppContext} from "@/contexts/app";

interface ActionCellProps {
    report: Report,
    type: "download" | "view" | "print"
    action: (id: string, type: "download" | "view" | "print") => void,
    inProgress: boolean,
    content: string,
    className: string,
    iconClassName: string,
    disabled: boolean,
}

function ActionCell(
    {
        report,
        type,
        action,
        inProgress,
        content,
        className,
        iconClassName,
        disabled
    }: ActionCellProps) {

    const handleClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
        ev.preventDefault();
        ev.stopPropagation();
        action(report.id, type);
    };

    return (
        <OverlayTrigger
            key={`tooltip-${report.id}`}
            placement="top"
            overlay={
                <Tooltip>
                    {content}
                </Tooltip>
            }>
            <Button
                disabled={disabled || false}
                className={className}
                onClick={handleClick}>
                {inProgress
                    ? <Spinner size="sm" className="green-spinner tiny" animation="border"/>
                    : <i className={iconClassName} aria-hidden="true"/>
                }
            </Button>
        </OverlayTrigger>
    );
}

interface EditCellProps {
    disabled: boolean
    row: RowInfo<Report>
    action: (ri: RowInfo<Report>) => void
    className: string
    iconClassName: string
}

function EditCell({disabled, row, action, className, iconClassName}: EditCellProps) {

    const handleClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
        ev.preventDefault();
        ev.stopPropagation();
        action(row);
    };

    const report = row.original as Report;
    return (
        <OverlayTrigger
            key={`tooltip-${report.id}`}
            overlay={
                <Tooltip>
                    Edit
                </Tooltip>
            }>
            <Button
                disabled={disabled || false}
                className={className}
                onClick={handleClick}>
                <i className={iconClassName} aria-hidden="true"/>
            </Button>
        </OverlayTrigger>
    );
}

interface ReportListState {
    currentReport?: Report
    reportUrl?: string
    pageSize: number
    pageNumber: number
    filteredReports?: number
    expand?: {[index: number]: boolean}
    showFilters: boolean
    isEdit: boolean
    actionType?: string,
    shouldShowPrivacyModal: boolean
    actionReportId?: string
    downloadingReport?: string
    isDownloading: boolean
    viewingReport?: string
    isViewing: boolean
    printingReport?: string
    isPrinting: boolean
}

export default function ReportListPage() {
    const defaultPage = Defaults.tablePage();
    const defaultPageSize = Defaults.tablePageSize();
    const {data: reports, mutate: mutateReports} = useReports();
    const eaiClient = new EAIClient();
    const appContext = useAppContext();

    const [state, setState] = useState<ReportListState>({
        currentReport: undefined,
        reportUrl: undefined,
        pageNumber: defaultPage,
        pageSize: defaultPageSize,
        filteredReports: undefined,
        showFilters: false,
        expand: undefined,
        isEdit: false,
        shouldShowPrivacyModal: false,
        actionType: undefined,
        actionReportId: undefined,
        downloadingReport: undefined,
        isDownloading: false,
        viewingReport: undefined,
        isViewing: false,
        printingReport: undefined,
        isPrinting: false,
    });

    const columns: ColumnDef<Report>[] = [
        {
            header: "Created",
            id: "created",
            accessorFn: (report: Report) => { return report.created; },
            cell: (props: CellContext<Report, unknown>) => <span>{Utils.getValidDateFromString(props.row.original.created.toString(), "MM/DD/YY")}</span>,
            filterFn: isWithinRangeFilter,
        },
        {
            header: "Report Name",
            accessorKey: "name",
            size: 400,
        },
        {
            header: "Creator",
            accessorKey: "created_by_name",
            size: 200,
        },
        {
            header: "ActionPlans",
            accessorKey: "tickets",
            cell: props => {
                const report = props.row.original;
                return (<span>{report.tickets.length}</span>);
            },
            maxSize: 150,
            enableColumnFilter: false,
        },
        {
            header: "Download",
            accessorKey: "download",
            cell: (cell: CellContext<Report, unknown>) => {
                const report = cell.row.original as Report;
                return (<ActionCell
                    disabled={!report.permissions.can_download}
                    report={report}
                    type="download"
                    action={ openPrivacyModal }
                    inProgress={ state.downloadingReport === report.id && state.isDownloading }
                    content="Download"
                    className="btn-icon"
                    iconClassName="download-button icon icon-download3" />);
            },
            id: "download",
            size: 100,
            enableColumnFilter: false,
            enableSorting: false
        },
        {
            header: "View",
            accessorKey: "view",
            cell: (cell: CellContext<Report, unknown>) => {
                const report = cell.row.original as Report;
                return (<ActionCell
                    disabled={!report.permissions.can_view}
                    report={report}
                    type="view"
                    action={ openPrivacyModal }
                    inProgress={ state.viewingReport === report.id && state.isViewing }
                    content="View"
                    className="btn-icon"
                    iconClassName="view-button icon icon-search2" />);
            },
            id: "view",
            size: 100,
            enableColumnFilter: false,
            enableSorting: false
        },
        {
            header: "Print",
            accessorKey: "print",
            cell: (cell: CellContext<Report, unknown>) => {
                const report = cell.row.original;
                return (<ActionCell
                    disabled={!report.permissions.can_print}
                    report={report}
                    type="print"
                    action={ openPrivacyModal }
                    inProgress={ state.printingReport === report.id && state.isPrinting }
                    content="Print"
                    className="btn-icon"
                    iconClassName="print-button icon icon-printer" />);
            },
            id: "print",
            size: 100,
            enableColumnFilter: false,
            enableSorting: false
        },
        {
            header: "Edit",
            accessorKey: "edit",
            cell: (cell: CellContext<Report, unknown>) => {
                const report = cell.row.original;

                return (<EditCell
                    disabled={!report.permissions.can_edit}
                    row={cell.row}
                    action={ editReport }
                    className="btn-icon"
                    iconClassName="icon icon-edit" />);
            },
            id: "expander",
            size: 100,
            enableColumnFilter: false,
            enableSorting: false
        },
    ];

    const getReportUrl = async (id: string) => {

        try {
            const resp = await eaiClient.reportUrl(id);
            setState(prevState => ({
                ...prevState,
                reportUrl: resp.data.data.pdf_url
            }));
            if (resp.data.data.pdf_url) {
                window.open(resp.data.data.pdf_url, "_blank");
            }
        } catch (e) {
            appContext.dispatchError(e);
        }
    };

    const printReport = async (id: string) => {
        try {
            setState(prevState => ({...prevState, isPrinting: true, printingReport: id}));
            await eaiClient.printReport(id);
        } catch (e) {
            appContext.dispatchError(e);
        } finally {
            setState(prevState => ({...prevState, printingReport: "", isPrinting: false}));
        }
    };

    const downloadReport = async (id: string) => {
        try {
            setState(prevState => ({...prevState, isDownloading: true, downloadingReport: id}));
            await eaiClient.downloadReport(id);
        } catch (e) {
            appContext.dispatchError(e);
        } finally {
            setState(prevState => ({...prevState, downloadingReport: "", isDownloading: false}));
        }
    };

    const editReport = (rowInfo: RowInfo<Report>) => {
        const isExpanded = rowInfo.getIsExpanded();
        rowInfo.getToggleExpandedHandler()();
        setState(prevState => ({...prevState, isEdit: !isExpanded}));
    };

    const openPrivacyModal = (id: string, actionType: "download" | "view" | "print") => {
        setState(prevState => ({
            ...prevState,
            shouldShowPrivacyModal: true,
            actionType: actionType,
            actionReportId: id
        }));
    };

    const handleAcceptPrivacyModal = () => {
        const {actionType, actionReportId} = state;
        if (actionType && actionReportId) {
            switch (actionType) {
                case "download":
                    downloadReport(actionReportId);
                    break;
                case "view":
                    getReportUrl(actionReportId);
                    break;
                case "print":
                    printReport(actionReportId);
                    break;
            }
        }
        handleClosePrivacyModal();
    };

    const handleClosePrivacyModal = () => {
        setState(prevState => ({
            ...prevState,
            actionType: undefined,
            shouldShowPrivacyModal: false
        }));
    };

    const onEditDone = () => {
        setState(prevState => ({...prevState, isEdit: false}));
        mutateReports();
    };

    const onCancel = () => setState(prevState => ({...prevState, isEdit: false}));

    const pageForward = () => setState(prevState => ({...prevState, pageNumber: prevState.pageNumber + 1}));

    const pageBackward = () => setState(prevState => ({...prevState, pageNumber: prevState.pageNumber - 1}));

    const numberOfReports = () => {
        if (!reports) return 0;

        if (state.filteredReports !== undefined) {
            return state.filteredReports;
        }

        if (reports) {
            return reports.length;
        }

        return 0;
    };

    const showRange = () => {
        const current = (state.pageSize * (state.pageNumber + 1)) - state.pageSize + 1;
        const end = state.pageSize * (state.pageNumber + 1) > numberOfReports()
            ? numberOfReports()
            : state.pageSize * (state.pageNumber + 1);
        const total = numberOfReports();

        return `${current} - ${end} of ${total}`;
    };

    const isEmpty = () => !reports || reports.length <= 0;

    const listName = () => `list-container reporting ${isEmpty() ? "empty" : ""} ${state.showFilters ? "show-filters" : ""}`;

    const toggleShowFilter = () => {
        const filteredReports = state.showFilters && reports ? numberOfReports() : state.filteredReports;
        setState(prevState => ({...prevState, filteredReports, showFilters: !state.showFilters}));
    };

    useEffect(() => {
        Utils.setViewName("ReportsList", "Reports");
        if (!reports) {
            return;
        }
        setState(prevState => ({...prevState, showFilters: true}));
    }, [reports]);

    if (!reports) {
        return (
            <div style={{textAlign: "center", paddingTop: 180}}>
                <Spinner variant="secondary" animation="border"/>
            </div>
        );
    }

    const noReports = reports && reports.length === 0;

    return (
        <React.Fragment>
            <Row className="section-action-bar">
                <Col md={12} lg={6} className="title">
                    <h3 id="reportsTitle">Reports</h3>
                </Col>
                <Col className="actions">
                    {Utils.hasAdminAccess() && (
                        <NavLink
                            id="report-create-link"
                            to={`${Utils.getLinkBase()}/reports/create`}
                        >
                            Create Report
                        </NavLink>
                    )}
                    {!noReports ? (
                        <Button id="report-filter-button" title="Filter" onClick={toggleShowFilter}>
                            <i className="icon icon-filter"></i>
                        </Button>
                    ) : null}
                </Col>
            </Row>
            <div className={listName()}>
                {noReports ? (
                    <Alert variant="light">No reports available.</Alert>
                ) : (
                    <React.Fragment>
                        <div className="action-container">
                            <div className="list-actions box-shadow">
                                <Row className="align-items-center">
                                    <Col>
                                        <div className="action float-end">
                                            <div className="arrows">
                                                <button
                                                    onClick={pageBackward}
                                                    className="back"
                                                    data-test-id="reports-page-backward"
                                                    disabled={state.pageNumber <= 0}
                                                >
                                                    <i className="icon icon-angle-left"/>
                                                </button>
                                                <button
                                                    onClick={pageForward}
                                                    className="forward"
                                                    data-test-id="reports-page-forward"
                                                    disabled={Utils.isNextButtonDisabled(
                                                        state.pageNumber,
                                                        state.pageSize,
                                                        numberOfReports()
                                                    )}
                                                >
                                                    <i className="icon icon-angle-right"/>
                                                </button>
                                            </div>
                                            <div className="range">{showRange()}</div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </div>
                        <ReportsListTable
                            columns={columns}
                            defaultData={reports ? reports.slice() : []}
                            showFilters={state.showFilters}
                            isEdit={state.isEdit}
                            onCancel={onCancel}
                            onEditDone={onEditDone}
                        />
                        <Modal show={state.shouldShowPrivacyModal} centered={true}>
                            <Modal.Body>
                                <div className="alert-icon text-center">
                                    <i className="icon icon-exclamation-circle"/>
                                </div>
                                <div className="content">
                                    You are about to download data from the secure Enflux system. Please make sure your
                                    school&apos;s data goes to a secure location.
                                </div>
                            </Modal.Body>
                            <Modal.Footer>
                                <div className="alert-action">
                                    <Button variant="secondary" onClick={handleClosePrivacyModal}
                                            style={{marginRight: 10}}>
                                        Cancel
                                    </Button>
                                    <Button variant="primary" onClick={handleAcceptPrivacyModal}
                                            id="okToDownloadButton">
                                        OK
                                    </Button>
                                </div>
                            </Modal.Footer>
                        </Modal>
                    </React.Fragment>
                )}
            </div>
        </React.Fragment>
    );
}

interface ReportsListTableProps {
    columns: ColumnDef<Report>[]
    defaultData: Report[]
    showFilters: boolean
    isEdit: boolean
    onCancel: () => void
    onEditDone: () => void
}

function ReportsListTable(
    {
      columns,
      defaultData,
      showFilters,
      isEdit,
      onCancel,
      onEditDone}: ReportsListTableProps) {
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
        []
    );
    const [data] = useState(() => [...defaultData]);
    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getRowCanExpand: () => true,
        state: {
            columnFilters,
            sorting,
        },
        onColumnFiltersChange: setColumnFilters,
        onSortingChange: setSorting,
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <div className="list">
            <PageTitle pageTitle="Reports" />
            <Table>
                <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => {
                            return (
                                <th key={header.id} colSpan={header.colSpan}>
                                    {header.isPlaceholder ? null : (
                                        <>
                                            <div
                                                className={header.column.getCanSort() ? "cursor-pointer " + (header.column.getIsSorted() || "") : ""}
                                                onClick={header.column.getToggleSortingHandler()}>
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                            </div>
                                            {showFilters ? (
                                                header.column.getCanFilter() && (
                                                    <div>
                                                        <Filter column={header.column} table={table} />
                                                    </div>) || <div className="fill-no-filter"></div>
                                            ) : null}
                                        </>
                                    )}
                                </th>
                            );
                        })}
                    </tr>
                ))}
                </thead>
                <tbody>
                {table.getRowModel().rows.map(row => {
                    return (
                        <Fragment key={row.id}>
                            <tr className={row.getIsExpanded() ? "zoom-out" : "zoom-in"} onClick={row.getToggleExpandedHandler()}>
                                {/* first row is a normal row */}
                                {row.getVisibleCells().map(cell => {
                                    return (
                                        <td key={cell.id}>
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                            {row.getIsExpanded() && (
                                <tr>
                                    {/* 2nd row is a custom 1 cell row */}
                                    <td colSpan={row.getVisibleCells().length}>
                                        {renderSubComponent({
                                            row,
                                            // reportStore,
                                            isEdit,
                                            onCancel,
                                            onEditDone})}
                                    </td>
                                </tr>
                            )}
                        </Fragment>
                    );
                })}
                </tbody>
            </Table>
        </div>

    );
}

type renderSubComponentProps = {
    row: RowInfo<Report>
    isEdit: boolean
    onCancel: () => void
    onEditDone: () => void
}
const renderSubComponent = ({ row, isEdit, onCancel, onEditDone }: renderSubComponentProps) => {
    return (
        <ReportDetails
            report={row.original}
            canEdit={row.original.permissions.can_edit}
            isEdit={isEdit}
            onCancel={onCancel}
            onEditDone={onEditDone} />
    );
};

function Filter(
    {
        column,
    }: {
        column: Column<any, unknown>
        table: ReactTable<any> }) {
    const columnFilterValue = column.getFilterValue();
    const onChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const date = new Date();
        const today = new Date();
        switch (e.target.value) {
            case "last_month": {
                const oneMonth = new Date(date.setMonth(date.getMonth() - 1));
                column.setFilterValue(() => [oneMonth, today]);
                break;
            }
            case "last_three_months": {
                const threeMonths = new Date(date.setMonth(date.getMonth() - 3));
                column.setFilterValue(() => [threeMonths, today]);
                break;
            }
            case "last_six_months": {
                const sixMonths = new Date(date.setMonth(date.getMonth() - 6));
                column.setFilterValue(() => [sixMonths, today]);
                break;
            }
            case "last_nine_months": {
                const nineMonths = new Date(date.setMonth(date.getMonth() - 9));
                column.setFilterValue(() => [nineMonths, today]);
                break;
            }
            case "last_twelve_months": {
                const twelveMonths = new Date(date.setMonth(date.getMonth() - 12));
                column.setFilterValue(() => [twelveMonths, today]);
                break;
            }
            default: {
                column.setFilterValue(() => []);
            }
        }
    };

    return column.id === "created" ? (
        <Form.Select aria-label="All" onChange={onChange}>
            <option value="all">All</option>
            <option value="last_month">Last Month</option>
            <option value="last_three_months">Last 3 Months</option>
            <option value="last_six_months">Last 6 Months</option>
            <option value="last_nine_months">Last 9 Months</option>
            <option value="last_twelve_months">Last 12 Months</option>
        </Form.Select>) : (
        <DebouncedInput
            type="text"
            value={(columnFilterValue ?? "") as string}
            onChange={value => column.setFilterValue(value)}
            className="w-36 border shadow rounded"
        />);
}

